import type { CheckoutSessionDisplayLine } from 'pi-kiosk-shared';
import type { CheckoutSessionLineDTO } from '../checkoutApi.js';
import { formatSellerLegalAddress } from '../../seller-legal/formatSellerLegalAddress.js';
import type { PublicSellerLegalDTO } from '../../seller-legal/publicSellerLegalTypes.js';

export type PrecontractShellSlotValue = {
  /** Resolved display text, or null → UI shows i18n placeholder. */
  readonly value: string | null;
};

export type PrecontractShellSlots = {
  readonly mainCharacteristics: PrecontractShellSlotValue;
  readonly sellerIdentity: PrecontractShellSlotValue;
  readonly totalAndFulfillmentTerms: PrecontractShellSlotValue;
};

function formatAmountMinor(amountMinor: number, currency: string): string {
  const major = amountMinor / 100;
  const formatted = Number.isInteger(major) ? major.toString() : major.toFixed(2);
  return `${formatted} ${currency}`;
}

function formatCharacteristicsFromDisplayLines(
  lines: readonly CheckoutSessionDisplayLine[],
): string | null {
  if (lines.length === 0) {
    return null;
  }
  return lines
    .map((line) => `${line.name} × ${line.quantity}`)
    .join('; ');
}

function formatCharacteristicsFromSessionLines(
  lines: readonly CheckoutSessionLineDTO[],
): string | null {
  if (lines.length === 0) {
    return null;
  }
  return lines
    .map((line) => `product:${line.productId} × ${line.quantity}`)
    .join('; ');
}

function formatSellerIdentity(sellerLegal: PublicSellerLegalDTO | null | undefined): string | null {
  if (sellerLegal == null) {
    return null;
  }
  const name = sellerLegal.legalName.trim();
  if (name.length === 0) {
    return null;
  }
  const parts = [name];
  const ico = sellerLegal.ico.trim();
  if (ico.length > 0) {
    parts.push(`ico:${ico}`);
  }
  const dic = sellerLegal.dic?.trim();
  if (dic != null && dic.length > 0) {
    parts.push(`dic:${dic}`);
  }
  const addr = formatSellerLegalAddress(sellerLegal.registeredAddress);
  if (addr != null && addr.length > 0) {
    parts.push(addr);
  }
  return parts.join(' · ');
}

/**
 * Pure slot builder for §3.6 precontract technical shell.
 * Neutral technical values only — no LEGAL prose.
 */
export function buildPrecontractShellSlots(input: {
  readonly displayLines?: readonly CheckoutSessionDisplayLine[] | null;
  readonly lines?: readonly CheckoutSessionLineDTO[] | null;
  readonly sellerLegal?: PublicSellerLegalDTO | null;
  readonly amountMinor: number;
  readonly currency: string;
  /** Technical fulfillment label already resolved by caller (mode / collect). */
  readonly fulfillmentTermsLabel?: string | null;
}): PrecontractShellSlots {
  const fromDisplay =
    input.displayLines != null && input.displayLines.length > 0
      ? formatCharacteristicsFromDisplayLines(input.displayLines)
      : null;
  const fromLines =
    fromDisplay == null && input.lines != null
      ? formatCharacteristicsFromSessionLines(input.lines)
      : null;

  const totalPart = formatAmountMinor(input.amountMinor, input.currency);
  const fulfillment = input.fulfillmentTermsLabel?.trim() ?? '';
  const totalAndFulfillment =
    fulfillment.length > 0 ? `${totalPart} · ${fulfillment}` : totalPart;

  return {
    mainCharacteristics: { value: fromDisplay ?? fromLines },
    sellerIdentity: { value: formatSellerIdentity(input.sellerLegal) },
    totalAndFulfillmentTerms: { value: totalAndFulfillment },
  };
}
